---
title: "Team accounts and roles"
description: "What Owners, Admins, and Viewers can each do in Chosen, how to invite teammates, and why most hiring managers should be Viewers."
category: "getting-started"
keywords:
  - "ATS user roles"
  - "recruiter permissions"
  - "invite team members"
  - "hiring manager access"
schema: "HowTo"
nav:
  section: "getting-started"
  order: 10
  parent: "/getting-started"
---

Chosen has three roles: Owner, Admin, and Viewer. Most of your team should be Viewers. This page is what each role can do and how to invite people.

## The three roles

| Role | Can do | Can't do |
| --- | --- | --- |
| **Owner** | Everything, including deleting the organization. | — |
| **Admin** | Everything operational — jobs, candidates, feedback, integrations, exports, API keys, inviting members. | Deleting the org. |
| **Viewer** | Read everything. Leave interview feedback. See analytics. | Edit candidates, change settings, or send anything. |

The role that surprises people is **Viewer**. A Viewer can't change your data — but they *can* leave structured interview feedback. That's the whole point of the role: it's how you bring an interviewer in without handing them the controls.

## Inviting your team

<Steps>
  <li>Go to **Settings → People**.</li>
  <li>Click **Invite member** and enter their email address.</li>
  <li>Pick their role. You can change it later, so when in doubt pick the smaller one.</li>
  <li>They get an emailed invitation. Invite as many people as you need — there's no seat limit.</li>
</Steps>

## Hiring managers should usually be Viewers

A hiring manager who reviews candidates and leaves feedback for one role does not need Admin. Give them Viewer. They get the candidate profiles and the feedback forms — everything they need — without the ability to change a status, edit a job, or disconnect an integration by accident.

Reserve Admin for the people who run requisitions day to day.

## Changing a role

Go to **Settings → People**, find the person, and change their role. It takes effect immediately — the next page they load runs on the new permissions.

## Managing your own account

Everything about *your* login lives in **Settings → Account**.

- **Your name** — the name that appears on the emails and scheduling invites you send.
- **Change your email** — enter the new address and we email it a six-digit code. Type the code back in and the switch is done. The code goes to the *new* address on purpose: that's how we confirm it's really yours.
- **Change your password** — sends a reset to your email; follow it, pick a new password, and you're back in.
- **Switch organization** — jump between organizations you belong to, handy if you recruit for more than one company.
- **Delete my account** — removes your login, your access to every organization, and your personal details. Your organization's candidates and jobs stay put; they belong to the org, not to you. You'll type your email to confirm.

<Callout type="info" title="Deleting your account isn't the same as deleting the organization.">
Deleting your account removes *you* and your access. If you're the Owner and you want the whole organization and its data gone, that's a separate request — reach out to support.
</Callout>
